@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.`init`.InitKobwebContext
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.iregained.eventapp.components.layouts.MarkdownLayout as _com_iregained_eventapp_components_layouts_MarkdownLayout
import com.iregained.eventapp.components.layouts.PageLayout as _com_iregained_eventapp_components_layouts_PageLayout
import com.iregained.eventapp.pages.AboutPage as _com_iregained_eventapp_pages_AboutPage
import com.iregained.eventapp.pages.AdminDashboardPage as _com_iregained_eventapp_pages_AdminDashboardPage
import com.iregained.eventapp.pages.AdminIndexPage as _com_iregained_eventapp_pages_AdminIndexPage
import com.iregained.eventapp.pages.AdminLoginPage as _com_iregained_eventapp_pages_AdminLoginPage
import com.iregained.eventapp.pages.EventBookingPage as _com_iregained_eventapp_pages_EventBookingPage
import com.iregained.eventapp.pages.HomePage as _com_iregained_eventapp_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "IR Event Management"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.iregained.eventapp.components.layouts.MarkdownLayout",
            parentLayoutId = "com.iregained.eventapp.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.iregained.eventapp.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_iregained_eventapp_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.iregained.eventapp.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_iregained_eventapp_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "com.iregained.eventapp.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.iregained.eventapp.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_iregained_eventapp_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "com.iregained.eventapp.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.iregained.eventapp.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _com_iregained_eventapp_pages_AboutPage()
        }
        ctx.router.register(
            "/admin",
        ) { pageCtx -> 
            _com_iregained_eventapp_pages_AdminIndexPage()
        }
        ctx.router.register(
            "/admin/dashboard",
        ) { pageCtx -> 
            _com_iregained_eventapp_pages_AdminDashboardPage()
        }
        ctx.router.register(
            "/admin/login",
        ) { pageCtx -> 
            _com_iregained_eventapp_pages_AdminLoginPage()
        }
        ctx.router.register(
            "/events/{slug}",
        ) { pageCtx -> 
            _com_iregained_eventapp_pages_EventBookingPage()
        }

        com.iregained.eventapp.initFirebase(ctx)
    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.iregained.eventapp.theme.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.iregained.eventapp.theme.SubheadlineTextStyle)
        ctx.theme.registerStyle("page-content", com.iregained.eventapp.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("markdown", com.iregained.eventapp.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("shared-hero-image", com.iregained.eventapp.components.widgets.styles.SharedHeroImageStyle)
        ctx.theme.registerStyle("primary-button", com.iregained.eventapp.components.widgets.styles.PrimaryButtonStyle)
        ctx.theme.registerStyle("secondary-button", com.iregained.eventapp.components.widgets.styles.SecondaryButtonStyle)
        ctx.theme.registerStyle("header-secondary-action", com.iregained.eventapp.components.widgets.styles.HeaderSecondaryActionStyle)
        ctx.theme.registerStyle("yt-card-wrapper", com.iregained.eventapp.components.widgets.styles.YtCardWrapperStyle)
        ctx.theme.registerStyle("yt-poster-image", com.iregained.eventapp.components.widgets.styles.YtPosterImageStyle)
        ctx.theme.registerStyle("yt-poster-gradient", com.iregained.eventapp.components.widgets.styles.YtPosterGradientStyle)
        ctx.theme.registerStyle("yt-play-button", com.iregained.eventapp.components.widgets.styles.YtPlayButtonStyle)
        ctx.theme.registerStyle("yt-pulse-ring", com.iregained.eventapp.components.widgets.styles.YtPulseRingStyle)
        ctx.theme.registerStyle("yt-label-pill", com.iregained.eventapp.components.widgets.styles.YtLabelPillStyle)
        ctx.theme.registerStyle("yt-iframe", com.iregained.eventapp.components.widgets.styles.YtIframeStyle)
        ctx.theme.registerStyle("card-container", com.iregained.eventapp.components.sections.event_booking.styles.CardContainerStyle)
        ctx.theme.registerStyle("slot-pill", com.iregained.eventapp.components.sections.event_booking.styles.SlotPillStyle)
        ctx.theme.registerStyle("date-tab", com.iregained.eventapp.components.sections.event_booking.styles.DateTabStyle)
        ctx.theme.registerStyle("footer-link", com.iregained.eventapp.components.sections.footer.styles.FooterLinkStyle)
        ctx.theme.registerStyle("social-icon", com.iregained.eventapp.components.sections.footer.styles.SocialIconStyle)
        ctx.theme.registerStyle("social-proof-section", com.iregained.eventapp.components.sections.event_details.styles.SocialProofSectionStyle)
        ctx.theme.registerStyle("social-proof-logo", com.iregained.eventapp.components.sections.event_details.styles.SocialProofLogoStyle)
        ctx.theme.registerStyle("registration-container", com.iregained.eventapp.components.sections.event_details.styles.RegistrationContainerStyle)
        ctx.theme.registerStyle("registration-card", com.iregained.eventapp.components.sections.event_details.styles.RegistrationCardStyle)
        ctx.theme.registerStyle("modern-input", com.iregained.eventapp.components.sections.event_details.styles.ModernInputStyle)
        ctx.theme.registerStyle("hero-section", com.iregained.eventapp.components.sections.event_details.styles.HeroSectionStyle)
        ctx.theme.registerStyle("hero-container", com.iregained.eventapp.components.sections.event_details.styles.HeroContainerStyle)
        ctx.theme.registerStyle("protocol-card", com.iregained.eventapp.components.sections.event_details.styles.ProtocolCardStyle)
        ctx.theme.registerStyle("protocol-card-bottom-accent", com.iregained.eventapp.components.sections.event_details.styles.ProtocolCardBottomAccentStyle)
        ctx.theme.registerStyle("protocol-image", com.iregained.eventapp.components.sections.event_details.styles.ProtocolImageStyle)
        ctx.theme.registerStyle("workflow-container", com.iregained.eventapp.components.sections.event_details.styles.WorkflowContainerStyle)
        ctx.theme.registerStyle("workflow-grid-container", com.iregained.eventapp.components.sections.event_details.styles.WorkflowGridContainerStyle)
        ctx.theme.registerStyle("workflow-row-grid", com.iregained.eventapp.components.sections.event_details.styles.WorkflowRowGridStyle)
        ctx.theme.registerStyle("workflow-image-box", com.iregained.eventapp.components.sections.event_details.styles.WorkflowImageBoxStyle)
        ctx.theme.registerStyle("workflow-image", com.iregained.eventapp.components.sections.event_details.styles.WorkflowImageStyle)
        ctx.theme.registerStyle("workflow-content-column", com.iregained.eventapp.components.sections.event_details.styles.WorkflowContentColumnStyle)
        ctx.theme.registerStyle("workflow-overline", com.iregained.eventapp.components.sections.event_details.styles.WorkflowOverlineStyle)
        ctx.theme.registerStyle("workflow-step-title", com.iregained.eventapp.components.sections.event_details.styles.WorkflowStepTitleStyle)
        ctx.theme.registerStyle("workflow-step-desc", com.iregained.eventapp.components.sections.event_details.styles.WorkflowStepDescStyle)
        ctx.theme.registerStyle("nav-header", com.iregained.eventapp.components.sections.header.styles.NavHeaderStyle)
        ctx.theme.registerStyle("nav-link", com.iregained.eventapp.components.sections.header.styles.NavLinkStyle)
        ctx.theme.registerStyle("button", com.iregained.eventapp.components.sections.header.styles.ButtonStyle)
        ctx.theme.registerStyle("header-button", com.iregained.eventapp.components.sections.header.styles.HeaderButtonStyle)
        ctx.theme.registerStyle("admin-sidebar", com.iregained.eventapp.components.sections.admin_dashboard.styles.AdminSidebarStyle)
        ctx.theme.registerStyle("kanban-column", com.iregained.eventapp.components.sections.admin_dashboard.styles.KanbanColumnStyle)
        ctx.theme.registerStyle("patient-card", com.iregained.eventapp.components.sections.admin_dashboard.styles.PatientCardStyle)
        ctx.theme.registerVariant("-circle", com.iregained.eventapp.theme.CircleButtonVariant)
        ctx.theme.registerVariant("-uncolored", com.iregained.eventapp.theme.UncoloredButtonVariant)
        ctx.theme.registerKeyframes("yt-pulse", com.iregained.eventapp.components.widgets.styles.YtPulseAnimation)
        ctx.theme.registerKeyframes("hero-text-slide-right", com.iregained.eventapp.components.widgets.HeroTextSlideRightKeyframes)
        ctx.theme.registerKeyframes("hero-media-scale-up", com.iregained.eventapp.components.widgets.HeroMediaScaleUpKeyframes)
        ctx.theme.registerKeyframes("hero-fade-in", com.iregained.eventapp.components.sections.event_details.sections.HeroFadeInKeyframes)
        ctx.theme.registerKeyframes("side-menu-slide-in", com.iregained.eventapp.components.sections.header.styles.SideMenuSlideInAnim)
        com.iregained.eventapp.theme.initSiteStyles(ctx)
        com.iregained.eventapp.theme.initTheme(ctx)
        com.iregained.eventapp.initColorMode(ctx)
        com.iregained.eventapp.initStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.iregained.eventapp.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
